import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizedText } from '@mews/framework';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { anonymizeUrl } from '@mews-commander/utils';
import { Tab } from '@optimus-web/core';
import { trackEvent } from '../../middleware';
import { navigateInModal } from '../app/actions/common';
import { TabData, TabsConfig } from './types';

const useHandleTabClick = (onTabClick?: (e?: React.MouseEvent, url?: string) => void) => {
    const dispatch: CommanderThunkDispatch = useDispatch();

    return useCallback(
        (e: React.MouseEvent, url: string) => {
            dispatch(
                trackEvent({
                    eventCategory: 'Tab',
                    eventAction: 'link clicked',
                    eventLabel: anonymizeUrl(url),
                    eventValue: undefined,
                })
            );
            dispatch(navigateInModal(url));
            onTabClick?.(e, url);
        },
        [dispatch, onTabClick]
    );
};

export const useTabsConfig = ({
    tabs,
    activeTab,
    onTabClick,
}: {
    tabs: Array<TabData>;
    activeTab: Mews.TextKey;
    onTabClick?: (e?: React.MouseEvent, url?: string) => void;
}): TabsConfig => {
    const handleTabClick = useHandleTabClick(onTabClick);

    return {
        inline: true,
        handleTabClick,
        activeTabValue: tabs.find(({ key }) => key === activeTab)?.url ?? '',
        tabs: tabs.map(({ key, url, substitutes, 'data-test-tab': dataTestTab }) => (
            <Tab
                key={key}
                id={key}
                label={<LocalizedText textKey={key} substitutes={substitutes} />}
                dataTestTab={dataTestTab}
                value={url}
            />
        )),
    };
};

interface UseTabContentParameters<TabContent> {
    initialTabValue: string;
    tabs: Array<JSX.Element>;
    tabContentMap: Record<string, TabContent>;
}

export function useTabContent<TabContent>({
    initialTabValue,
    tabs,
    tabContentMap,
}: UseTabContentParameters<TabContent>) {
    const [activeTabValue, setActiveTabValue] = useState(initialTabValue);

    const handleTabClick = (_: unknown, value: string) => {
        setActiveTabValue(value);
    };

    const tabsConfig: TabsConfig = {
        activeTabValue,
        handleTabClick,
        inline: true,
        tabs,
    };

    return {
        tabsConfig,
        tabContent: tabContentMap[activeTabValue],
    };
}
